import React, { useMemo } from 'react';
import { X, Info, CheckCircle2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useDoctorRevenueBreakdownQuery } from '../../api/reportsApi';
import ChartBox from '../../components/ChartBox';
import ExportButtons from '../../components/ExportButtons';
import { doctorDisplayName } from '../appointments/doctorColors';

const fmtINR = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number.isFinite(+v) ? +v : 0);
const fmtINR2 = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number.isFinite(+v) ? +v : 0);

function Stat({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: 4, color: accent || 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function RowSkeleton() {
  return (
    <tr>
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i}><div className="skeleton-bar" style={{ height: 12, borderRadius: 4, background: 'var(--surface-alt, #eee)', opacity: 0.6 }} /></td>
      ))}
    </tr>
  );
}

/**
 * Drill-down drawer that unpacks exactly how a doctor's revenue (Rule #1) was compiled over the
 * selected IST range.
 *
 * One row per patient × PACKAGE: the package's own value, its own session count, the rate that
 * follows (value ÷ sessions), how many of those sessions this doctor took in range, and the
 * revenue that results (rate × count). Rows sum to the doctor's attributed total. A patient who
 * drew on two packages appears twice — deliberately, since each package has its own rate.
 */
export default function DoctorRevenueDrilldown({ userId, range, onClose }) {
  // Spread the full range so the global Exclude-Homeopathy toggle reaches this query as well.
  const { data, isFetching, isError } = useDoctorRevenueBreakdownQuery(
    { userId, ...(range ?? {}) },
    { skip: !userId },
  );
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const chartData = useMemo(
    () => rows.slice(0, 8).map((r) => ({ name: r.patientName?.split(' ')[0] || r.patientCode, revenue: r.attributedRevenue })),
    [rows],
  );

  if (!userId) return null;

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isFetching && !data ? 'Loading…' : `${doctorDisplayName(data?.doctorName ?? 'Doctor')} — Revenue Breakdown`}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {rows.length > 0 && (
              <ExportButtons
                filename={`doctor-revenue-${(data?.doctorName || 'doctor').replace(/\s+/g, '-').toLowerCase()}`}
                title={`Revenue Breakdown — ${doctorDisplayName(data?.doctorName)}`}
                subtitle="Revenue by patient and package (selected range)"
                orientation="landscape"
                columns={[
                  { key: 'patient', label: 'Patient' }, { key: 'uhid', label: 'UHID' },
                  { key: 'package', label: 'Package' }, { key: 'purchasedAt', label: 'Package Bought' },
                  // Package value and its session count describe each package, so summing them
                  // across rows says nothing — a package listed twice for two doctors would be
                  // double-counted. Only the doctor's own sessions and revenue are additive.
                  { key: 'packageValue', label: 'Package Value', numeric: true, money: true, total: false },
                  { key: 'purchased', label: 'Sessions in Package', numeric: true, total: false },
                  // No column-wise average: the plain mean of the per-package rates weights a
                  // 1-session package the same as a 50-session one. The blended rate that actually
                  // means something is revenue ÷ sessions, reported in the summary line below.
                  { key: 'baseCost', label: 'Rate / Session', numeric: true, money: true, total: false },
                  { key: 'byDoctor', label: 'Sessions (this doctor)', numeric: true },
                  { key: 'attributed', label: 'Attributed Revenue', numeric: true, money: true },
                  { key: 'renewal', label: 'Renewal Pitched By' },
                ]}
                getRows={() => rows.map((r) => ({
                  patient: r.patientName, uhid: r.patientCode,
                  package: r.isLegacyBasis ? `${r.packageName ?? '—'} (untraced)` : (r.packageName ?? '—'),
                  purchasedAt: r.packagePurchasedAt
                    ? new Date(r.packagePurchasedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : '—',
                  packageValue: r.packageValue, purchased: r.sessionsPurchased,
                  baseCost: r.baseSessionCost, byDoctor: r.sessionsByDoctor,
                  attributed: r.attributedRevenue,
                  renewal: r.renewalPitchedByName || (r.renewalPitched && r.renewalPitched !== 'NotYet' ? r.renewalPitched : '—'),
                }))}
                getSummary={() => {
                  const rev = Number(data?.totalAttributedRevenue ?? 0);
                  const sess = Number(data?.totalSessionsAttended ?? 0);
                  return [
                    { label: 'Total attributed revenue', value: `INR ${Math.round(rev).toLocaleString('en-IN')}` },
                    { label: 'Total sessions attended', value: sess },
                    // Weighted blended rate — the only average worth quoting here.
                    { label: 'Average revenue per session', value: `INR ${sess ? Math.round(rev / sess).toLocaleString('en-IN') : 0}` },
                    { label: 'Patients', value: data?.patientCount ?? 0 },
                    { label: 'Unpriced sessions (fallback rate used)', value: data?.unpricedSessions ?? 0 },
                  ];
                }}
              />
            )}
            <button className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>

        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            <Stat label="Attributed revenue" value={fmtINR(data?.totalAttributedRevenue)} accent="var(--green-700)" />
            <Stat label="Sessions attended" value={data?.totalSessionsAttended ?? 0} />
            <Stat label="Patients" value={data?.patientCount ?? 0} />
            <Stat
              label="Unpriced sessions"
              value={data?.unpricedSessions ?? 0}
              accent={(data?.unpricedSessions ?? 0) > 0 ? 'var(--amber-700, #b45309)' : undefined}
            />
          </div>

          <div className="info-banner" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '12px 0', padding: '8px 10px', background: 'var(--blue-50, #eff6ff)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            <Info size={15} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Each session is valued at the rate of the <strong>package it was taken from</strong> —
              <strong> rate = package value ÷ sessions in that package</strong> — then multiplied by how many of
              those sessions <em>this doctor</em> completed inside the selected range. Nothing is blended across
              packages, and a package bought before the range still counts for the sessions used during it.
              Rows marked <span className="badge b-gray">untraced</span> could not be linked to a priced package,
              so the patient's overall billed rate was used instead.
            </span>
          </div>

          {chartData.length > 0 && (
            <ChartBox height={200}>
              <BarChart data={chartData} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="revenue" name="Attributed revenue" fill="var(--green-500)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartBox>
          )}

          <div className="tbl-wrap" style={{ marginTop: 14 }}>
            <table>
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Package</th>
                  <th style={{ textAlign: 'right' }}>Package Value</th>
                  <th style={{ textAlign: 'right' }}>Sessions in Package</th>
                  <th style={{ textAlign: 'right' }}>Rate / Session</th>
                  <th style={{ textAlign: 'right' }}>Sessions (this Dr.)</th>
                  <th style={{ textAlign: 'right' }}>Attributed Revenue</th>
                  <th>Renewal Pitched By</th>
                </tr>
              </thead>
              <tbody>
                {isFetching && !data && Array.from({ length: 5 }).map((_, i) => <RowSkeleton key={i} />)}

                {!isFetching && isError && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--red-700)' }}>Could not load the breakdown.</td></tr>
                )}

                {data && rows.map((r) => (
                  <tr key={`${r.patientId}-${r.packageName ?? 'none'}-${r.packagePurchasedAt ?? ''}`}>
                    <td className="td-bold">
                      {r.patientName}
                      <div className="td-muted" style={{ fontSize: '0.72rem' }}>{r.patientCode}</div>
                    </td>
                    <td>
                      {r.packageName ?? '—'}
                      {r.isLegacyBasis && <span className="badge b-gray" style={{ marginLeft: 6 }}>untraced</span>}
                      {r.packagePurchasedAt && (
                        <div className="td-muted" style={{ fontSize: '0.72rem' }}>
                          bought {new Date(r.packagePurchasedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.isLegacyBasis ? '—' : fmtINR(r.packageValue)}</td>
                    <td style={{ textAlign: 'right' }}>{r.isLegacyBasis ? '—' : r.sessionsPurchased}</td>
                    <td style={{ textAlign: 'right' }}>{fmtINR2(r.baseSessionCost)}</td>
                    <td style={{ textAlign: 'right' }}>{r.sessionsByDoctor}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="td-bold" style={{ color: 'var(--green-700)' }}>{fmtINR(r.attributedRevenue)}</span>
                      <div className="td-muted" style={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                        {fmtINR2(r.baseSessionCost)} × {r.sessionsByDoctor}
                      </div>
                    </td>
                    <td>
                      {r.renewalPitchedByName ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {r.renewalPitchedByThisDoctor && <CheckCircle2 size={13} color="var(--green-600)" />}
                          <span className={r.renewalPitchedByThisDoctor ? 'td-bold' : ''}>{doctorDisplayName(r.renewalPitchedByName)}</span>
                        </span>
                      ) : (
                        <span className="badge b-gray">{r.renewalPitched && r.renewalPitched !== 'NotYet' ? r.renewalPitched : '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}

                {data && rows.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)' }}>
                    No completed sessions attributed to this doctor in the selected range.
                  </td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border, #e5e7eb)', fontWeight: 700 }}>
                    <td>Total ({data?.patientCount} patients)</td>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>{data?.totalSessionsAttended}</td>
                    <td style={{ textAlign: 'right', color: 'var(--green-700)' }}>{fmtINR(data?.totalAttributedRevenue)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
